// Painted foe art (AI-painted cutout parts, tools/gen-parts.mjs + tools/cut-parts.mjs).
//
// Every regular enemy and boss can have a folder public/assets/foes/<id>/ holding one image per
// part (WebP via tools/foe-webp.mjs) plus rig.json (joint pivots in each part's own pixels).
// Enemies draw these parts onto their existing pose skeletons (src/systems/enemies/painted.ts);
// bosses repaint their existing part textures in place (src/systems/bosses/painted.ts). Anything
// missing falls back to the procedural art, so the game never depends on these files.

import type Phaser from 'phaser';

export type Pt = readonly [number, number];

export interface FoePart {
  img: HTMLImageElement | HTMLCanvasElement;
  w: number;
  h: number;
  pivots: Readonly<Record<string, Pt>>;
}

export interface FoeRig {
  id: string;
  parts: Readonly<Record<string, FoePart>>;
  /** Free-form per-foe tuning from rig.json ("tune"). */
  tune: Readonly<Record<string, number>>;
  /** The whole rig.json (setup-pose rigs read their pieces / joints from it). */
  json: Readonly<Record<string, unknown>>;
}

/** Parts each foe needs (all must load, or the foe keeps its procedural art). */
export const FOE_PARTS: Readonly<Record<string, readonly string[]>> = {
  trooper: ['head', 'torso', 'pelvis', 'thigh', 'shin', 'foot', 'gun'],
  lancer: ['backShin', 'backFoot', 'backThigh', 'frontShin', 'frontFoot', 'frontThigh', 'torso', 'gun'],
  bulwark: ['backShin', 'backFoot', 'backThigh', 'frontShin', 'frontFoot', 'frontThigh', 'armFore', 'armUpper', 'torso', 'shield'],
  hornet: ['body', 'wing', 'abd', 'tail', 'legs', 'spike'],
  warden: ['torso', 'head', 'pelvis', 'rack', 'thigh', 'shin', 'foot', 'antenna', 'core', 'shutterTop', 'shutterBot', 'rackLid'],
  titan: ['hull', 'crucible', 'hammer', 'thruster', 'boom', 'hook', 'nozzle', 'heart', 'lid', 'piston', 'chain'],
  heart: ['core', 'plate'],
};

export const foeKeys = {
  rig: (id: string) => `foe_${id}_rig`,
  part: (id: string, p: string) => `foe_${id}_${p}`,
  path: (id: string, file: string) => `assets/foes/${id}/${file}`,
};

/** Queue every painted foe asset on a scene's loader (BootScene.preload). */
export function queueFoeAssets(scene: Phaser.Scene): void {
  for (const [id, parts] of Object.entries(FOE_PARTS)) {
    scene.load.json(foeKeys.rig(id), foeKeys.path(id, 'rig.json'));
    for (const p of parts) scene.load.image(foeKeys.part(id, p), foeKeys.path(id, `${p}.webp`));
  }
}

const cache = new Map<string, FoeRig | null>();

/** The painted rig of a foe, or null when its assets are missing (procedural fallback). */
export function foeRig(scene: Phaser.Scene, id: string): FoeRig | null {
  if (cache.has(id)) return cache.get(id) ?? null;
  const names = FOE_PARTS[id];
  const json = scene.cache.json.get(foeKeys.rig(id)) as
    | { mode?: string; parts?: Record<string, { pivots?: Record<string, [number, number]> }>; tune?: Record<string, number> }
    | undefined;
  let rig: FoeRig | null = null;
  // Skeleton rigs need per-part pivots; setup-pose rigs place pieces by origin and joints instead.
  const usable = !!json && (json.mode === 'setup' || !!json.parts);
  if (names && usable && names.every((p) => scene.textures.exists(foeKeys.part(id, p)))) {
    const parts: Record<string, FoePart> = {};
    for (const p of names) {
      const img = scene.textures.get(foeKeys.part(id, p)).getSourceImage() as HTMLImageElement;
      parts[p] = { img, w: img.width, h: img.height, pivots: json?.parts?.[p]?.pivots ?? {} };
    }
    rig = { id, parts, tune: json.tune ?? {}, json: json as Record<string, unknown> };
  }
  cache.set(id, rig);
  return rig;
}

/** Pivot of a part (falls back to the part centre). */
export function pivot(part: FoePart, name: string): Pt {
  return part.pivots[name] ?? [part.w / 2, part.h / 2];
}

const darkCache = new WeakMap<object, HTMLCanvasElement>();

/** A darkened copy of a part image (far-side limbs). */
export function darkened(part: FoePart, amount = 0.38): HTMLCanvasElement {
  let c = darkCache.get(part);
  if (c) return c;
  c = document.createElement('canvas');
  c.width = part.w;
  c.height = part.h;
  const g = c.getContext('2d')!;
  g.drawImage(part.img, 0, 0);
  g.globalCompositeOperation = 'source-atop';
  g.fillStyle = `rgba(8,10,16,${amount})`;
  g.fillRect(0, 0, part.w, part.h);
  darkCache.set(part, c);
  return c;
}

type G = CanvasRenderingContext2D;

/**
 * Draw a part so its pivot `a` lands on `from` and its pivot `b` points at `to`, uniformly
 * scaled so the a→b distance matches (a bone between two joints).
 */
export function drawBone(g: G, part: FoePart, a: string, b: string, from: { x: number; y: number }, to: { x: number; y: number }, img?: CanvasImageSource, stretch = 1): void {
  const [ax, ay] = pivot(part, a);
  const [bx, by] = pivot(part, b);
  const len = Math.hypot(bx - ax, by - ay) || 1;
  const k = (Math.hypot(to.x - from.x, to.y - from.y) / len) * stretch;
  const rot = Math.atan2(to.y - from.y, to.x - from.x) - Math.atan2(by - ay, bx - ax);
  g.save();
  g.translate(from.x, from.y);
  g.rotate(rot);
  g.scale(k, k);
  g.drawImage(img ?? part.img, -ax, -ay);
  g.restore();
}

/** Draw a part with pivot `a` at (x, y), rotated by `rot` and scaled by `k`. */
export function drawAt(g: G, part: FoePart, a: string, x: number, y: number, rot: number, k: number, img?: CanvasImageSource): void {
  const [ax, ay] = pivot(part, a);
  g.save();
  g.translate(x, y);
  g.rotate(rot);
  g.scale(k, k);
  g.drawImage(img ?? part.img, -ax, -ay);
  g.restore();
}

/** Distance between two pivots of a part (px). */
export function span(part: FoePart, a: string, b: string): number {
  const [ax, ay] = pivot(part, a);
  const [bx, by] = pivot(part, b);
  return Math.hypot(bx - ax, by - ay);
}

/** Angle of the a→b direction inside a part image. */
export function dirOf(part: FoePart, a: string, b: string): number {
  const [ax, ay] = pivot(part, a);
  const [bx, by] = pivot(part, b);
  return Math.atan2(by - ay, bx - ax);
}
